import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_ENDPOINTS, API_BASE_URL } from '../constants/apiConstants';
import { getStoredTokens } from '../utils/auth';
import './CSS/BusinessDNA.css';

const emptyHistory = [];

const formatTimestamp = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    console.error('Unable to format timestamp', error);
    return value;
  }
};

const formatDuration = (sec) => {
  if (sec === null || sec === undefined) return null;
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
};

const getDomain = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
};

/* ── Expandable scan card ── */
const ScanCard = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);

  const images = entry.imageList || [];
  const productLinks = entry.productPageLinks || [];
  const previewCount = 6;

  return (
    <div className={`scan-card ${expanded ? 'scan-card--expanded' : ''}`}>

      {/* ── Header strip: logo + domain + status ── */}
      <div className="scan-card-header">
        <div className="scan-card-identity">
          {entry.logoUrl ? (
            <img
              className="scan-logo"
              src={entry.logoUrl}
              alt={`${getDomain(entry.url)} logo`}
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = e.target.parentElement.querySelector('.scan-logo-placeholder');
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="scan-logo-placeholder"
            style={{ display: entry.logoUrl ? 'none' : 'flex' }}
          >
            {getDomain(entry.url).charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="scan-domain">{getDomain(entry.url)}</p>
            <a
              className="scan-url-link"
              href={entry.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {entry.url}
            </a>
          </div>
        </div>
        <span className={`status-pill ${entry.status === 'Completed' ? 'success' : 'warning'}`}>
          {entry.status || 'Pending'}
        </span>
      </div>

      {/* ── OG Image banner ── */}
      {entry.ogImage && (
        <div className="scan-og-image-row">
          <img
            src={entry.ogImage}
            alt="OG preview"
            className="scan-og-image"
            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
          />
        </div>
      )}

      {/* ── Theme color chips ── */}
      {entry.themeColors && (
        <div className="scan-theme-colors-row">
          <span className="scan-section-label">Theme</span>
          <div className="color-chips-wrap">
            {[
              { label: 'Primary', color: entry.themeColors.primary },
              { label: 'Secondary', color: entry.themeColors.secondary },
              { label: 'Accent', color: entry.themeColors.accent },
              { label: 'Background', color: entry.themeColors.background },
              { label: 'Text', color: entry.themeColors.text },
            ].filter(c => c.color).map(({ label, color }) => (
              <div key={label} className="color-chip" title={`${label}: ${color}`}>
                <div className="color-chip-swatch" style={{ background: color }} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Brand color palette ── */}
      {entry.brandColors && entry.brandColors.length > 0 && (
        <div className="scan-palette-row">
          <span className="scan-section-label">Palette</span>
          <div className="scan-palette">
            {entry.brandColors.map((color) => (
              <div
                key={color}
                className="palette-swatch"
                style={{ background: color }}
                title={color}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Page title + description ── */}
      {(entry.title || entry.description) && (
        <div className="scan-meta-block">
          {entry.title && <p className="scan-page-title">{entry.title}</p>}
          {entry.description && (
            <p className="scan-page-desc">
              {entry.description.length > 160
                ? entry.description.slice(0, 160) + '…'
                : entry.description}
            </p>
          )}
        </div>
      )}

      {/* ── Keywords ── */}
      {entry.keywords && entry.keywords.length > 0 && (
        <div className="scan-keywords-row">
          <span className="scan-section-label">Keywords</span>
          <div className="keyword-chips">
            {entry.keywords.map((kw) => (
              <span key={kw} className="keyword-chip">{kw}</span>
            ))}
          </div>
        </div>
      )}

      {/* ── SEO quick info ── */}
      <div className="scan-seo-row">
        {entry.canonical && <span title={entry.canonical}>✅ Canonical</span>}
        {entry.ogImage && <span>✅ OG Image</span>}
        {entry.language && <span>🌐 {entry.language}</span>}
        {entry.charset && <span>🔤 {entry.charset}</span>}
        {entry.ogType && <span>📄 {entry.ogType}</span>}
      </div>

      {/* ── Image gallery preview ── */}
      {images.length > 0 && (
        <div className="scan-images-section">
          <div className="scan-section-header">
            <span className="scan-section-label">
              Scraped Images ({images.length}{entry.imageCount && entry.imageCount > images.length ? ` of ${entry.imageCount}` : ''})
            </span>
            {entry.imageCount && entry.imageCount > images.length && (
              <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '0.5rem' }}>
                (Re-scan to load all {entry.imageCount} images)
              </span>
            )}
          </div>
          <div className="scan-image-grid">
            {images.slice(0, expanded ? images.length : previewCount).map((img, i) => {
              const url = typeof img === 'string' ? img : img.url;
              if (!url) return null;
              return (
                <div
                  key={url + i}
                  className="scan-image-thumb"
                  onClick={() => setLightboxImg(url)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setLightboxImg(url);
                    }
                  }}
                >
                  <img
                    src={url}
                    alt={img.alt || `Image ${i + 1}`}
                    loading="lazy"
                    onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                  />
                  {img.strategy && <span className="img-strategy-badge">{img.strategy}</span>}
                </div>
              );
            })}
          </div>
          {images.length > previewCount && !expanded && (
            <button
              type="button"
              className="show-more-btn"
              onClick={() => setExpanded(true)}
            >
              Show all {images.length} images
            </button>
          )}
        </div>
      )}

      {/* ── Product page links ── */}
      {productLinks.length > 0 && expanded && (
        <div className="scan-product-links">
          <span className="scan-section-label">Product Pages ({productLinks.length})</span>
          <ul className="product-link-list">
            {productLinks.map((link) => (
              <li key={link}>
                <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Stats row ── */}
      <div className="scan-stats">
        {entry.score !== null && entry.score !== undefined && (
          <div className="stat-chip">
            <span className="stat-value">{entry.score}</span>
            <span className="stat-label">Score</span>
          </div>
        )}
        {entry.imageCount > 0 && (
          <div className="stat-chip">
            <span className="stat-value">{entry.imageCount}</span>
            <span className="stat-label">Images</span>
          </div>
        )}
        {entry.pageCount > 0 && (
          <div className="stat-chip">
            <span className="stat-value">{entry.pageCount}</span>
            <span className="stat-label">Pages Found</span>
          </div>
        )}
        {formatDuration(entry.durationSec) && (
          <div className="stat-chip">
            <span className="stat-value">{formatDuration(entry.durationSec)}</span>
            <span className="stat-label">Scan Time</span>
          </div>
        )}
      </div>

      {/* ── Footer: timestamp + expand toggle ── */}
      <div className="scan-card-footer">
        <span className="scan-time">Scanned {formatTimestamp(entry.scannedAt)}</span>
        {(images.length > previewCount || productLinks.length > 0) && (
          <button
            type="button"
            className="expand-toggle"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Collapse' : 'View details'}
          </button>
        )}
      </div>

      {/* ── Image lightbox ── */}
      {lightboxImg && (
        <div className="lightbox-overlay" onClick={() => setLightboxImg(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxImg(null)}>×</button>
            <img src={lightboxImg} alt="Full size" />
          </div>
        </div>
      )}
    </div>
  );
};

const BusinessDNA = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState(emptyHistory);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadHistory = async () => {
    setIsLoading(true);
    setError('');

    try {
      const tokens = getStoredTokens();

      if (!tokens?.idToken) {
        console.log('No auth token available, showing empty history');
        setHistory(emptyHistory);
        setIsLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.WEBPAGE_ANALYZER_HISTORY}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.idToken}`
        }
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        let errorMessage = 'Failed to load scan history';
        try {
          const errorData = await response.json();
          console.error('Backend Error Response:', errorData);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        setError(errorMessage);
        setHistory(emptyHistory);
        setIsLoading(false);
        return;
      }

      const result = await response.json();
      console.log('Scan history from database:', result);

      if (result.data && Array.isArray(result.data)) {
        setHistory(result.data);
      } else {
        setHistory(emptyHistory);
      }

    } catch (error) {
      console.error('Error loading scan history:', error);
      setError('Failed to load scan history. Please try again.');
      setHistory(emptyHistory);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  return (
    <div className="business-dna-page">
      <Navbar />
      <main className="dna-shell">
        <section className="dna-hero">
          <p className="eyebrow">Business DNA</p>
          <h1>Replay your web scanner results</h1>
          <p className="hero-copy">
            Launch a fresh scan of your site or revisit everything you have already scraped with the Web Scanner.
          </p>
          <div className="hero-actions">
            <button type="button" className="primary-btn" onClick={() => navigate('/webpage-analyzer')}>
              Run Web Scanner
            </button>
            <button type="button" className="ghost-btn" onClick={loadHistory}>
              Refresh History
            </button>
          </div>
        </section>

        <section className="history-panel">
          <div className="section-heading">
            <h2>Previously Scraped Websites</h2>
            <p>Each entry represents the latest completion from the Web Scanner service.</p>
          </div>

          {error && (
            <div className="dna-error-message">
              <p>{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="loading-container">
              <div className="dna-spinner"></div>
              <p>Loading your scan history...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="dna-empty">
              <h3>No scans yet</h3>
              <p>Run the Web Scanner to see your Business DNA archive populate here.</p>
              <button type="button" className="primary-btn" onClick={() => navigate('/webpage-analyzer')}>
                Start Your First Scan
              </button>
            </div>
          ) : (
            <div className="scan-cards">
              {history.map((entry) => (
                <ScanCard key={entry.id || entry._id} entry={entry} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default BusinessDNA;

